// Shader fills and effects for the renderers: requests go to the shader worker (GPU, sandboxed),
// results come back as ImageBitmaps. Each layer slot keeps its latest image, so while a new one
// renders the canvas shows the previous one instead of flickering to a placeholder.

import type { NodeId, ShaderProvider, ShaderRequest } from '@vellum/core';

export type ShaderProperties = Record<string, Record<string, unknown>>;

interface Slot {
  key: string;
  image: ImageBitmap | null;
  /** key of the request in flight */
  pending: string | null;
  /** newest request that arrived while one was in flight */
  queued: { key: string; req: ShaderRequest; input?: ImageData } | null;
  error: string | null;
}

export class ShaderRuntime implements ShaderProvider {
  private worker: Worker | null = null;
  private slots = new Map<string, Slot>();
  private versions = new Map<NodeId, number>();
  private waiting = new Map<number, (r: { bitmap?: ImageBitmap; error?: string; properties?: ShaderProperties }) => void>();
  private props = new Map<string, Promise<ShaderProperties>>();
  private nextId = 1;
  /** bumps whenever any shader result arrives */
  version = 0;
  private animated = new Map<string, boolean>();
  private ticks = new Map<string, ReturnType<typeof setTimeout>>();
  private t0 = typeof performance !== 'undefined' ? performance.now() : 0;
  /** Frame interval for animated shaders (ms). */
  frameMs = 33;

  constructor(private onChange: (nodeId: NodeId) => void) {}

  get available() {
    return typeof navigator !== 'undefined' && 'gpu' in navigator && typeof Worker !== 'undefined';
  }

  private ensureWorker(): Worker | null {
    if (this.worker || !this.available) return this.worker;
    this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'vellum-shaders' });
    this.worker.onmessage = (e: MessageEvent<{ id: number; bitmap?: ImageBitmap; error?: string; properties?: ShaderProperties }>) => {
      const cb = this.waiting.get(e.data.id);
      this.waiting.delete(e.data.id);
      cb?.(e.data);
    };
    return this.worker;
  }

  private call(msg: Record<string, unknown>, transfer: Transferable[] = []) {
    const w = this.ensureWorker();
    if (!w) return Promise.resolve({ error: 'WebGPU is not available here' } as { bitmap?: ImageBitmap; error?: string; properties?: ShaderProperties });
    const id = this.nextId++;
    return new Promise<{ bitmap?: ImageBitmap; error?: string; properties?: ShaderProperties }>((resolve) => {
      this.waiting.set(id, resolve);
      w.postMessage({ id, ...msg }, transfer);
    });
  }

  /** A per-layer version: renderers fold it into raster signatures so new results get drawn. */
  versionOf(id: NodeId) {
    return this.versions.get(id) ?? 0;
  }

  /** The last error of a layer's shader (compile or GPU validation), for the properties panel. */
  errorOf(id: NodeId, kind: 'fill' | 'effect', index: number) {
    return this.slots.get(`${id}:${kind}:${index}`)?.error ?? null;
  }

  /** Render one shader now, outside any layer (the shader host does this for headless renders). */
  async renderNow(job: { kind: 'fill' | 'effect'; source: string; params?: Record<string, unknown>; width: number; height: number; time?: number; input?: ImageData }): Promise<ImageBitmap | null> {
    const r = await this.call({ op: 'render', ...job, time: job.time ?? 0 }, job.input ? [job.input.data.buffer] : []);
    return r.bitmap ?? null;
  }

  /** A layer's latest shader pixels as a PNG data: URI (for SVG export). */
  dataUrl(id: NodeId, kind: 'fill' | 'effect', index: number): string | null {
    const img = this.slots.get(`${id}:${kind}:${index}`)?.image;
    if (!img || typeof document === 'undefined') return null;
    const c = document.createElement('canvas');
    c.width = img.width;
    c.height = img.height;
    c.getContext('2d')?.drawImage(img, 0, 0);
    return c.toDataURL('image/png');
  }

  /** The controls a shader declares with defineProperties. */
  properties(source: string): Promise<ShaderProperties> {
    let p = this.props.get(source);
    if (!p) {
      p = this.call({ op: 'describe', kind: 'fill', source, width: 1, height: 1 }).then((r) => {
        if (r.error) throw new Error(r.error);
        return r.properties ?? {};
      });
      p.catch(() => this.props.delete(source));
      this.props.set(source, p);
    }
    return p;
  }

  /** Shaders that read frame.time / deltaTime / frame animate (as Figma's isAnimated ones do). */
  isAnimated(source: string) {
    let a = this.animated.get(source);
    if (a === undefined) {
      a = /\bframe\s*\.\s*(time|deltaTime|frame)\b/.test(source);
      this.animated.set(source, a);
    }
    return a;
  }

  /** Paused animations render at their own time (or 0); playing ones follow the clock. */
  playing = true;

  get(req: ShaderRequest): CanvasImageSource | null {
    if (!this.available) return null;
    const slotKey = `${req.nodeId}:${req.kind}:${req.index}`;
    if (req.time === undefined && this.playing && this.isAnimated(req.source)) {
      // quantized clock: one request per frame interval, then ask to be drawn again next frame
      const now = performance.now() - this.t0;
      req = { ...req, time: Math.floor(now / this.frameMs) * this.frameMs };
      if (!this.ticks.has(slotKey))
        this.ticks.set(
          slotKey,
          setTimeout(() => {
            this.ticks.delete(slotKey);
            this.versions.set(req.nodeId, this.versionOf(req.nodeId) + 1);
            this.onChange(req.nodeId);
          }, this.frameMs - (now % this.frameMs) + 1),
        );
    }
    let input: ImageData | undefined;
    let inputKey = '';
    if (req.kind === 'effect' && req.input) {
      const ctx = req.input.getContext('2d') as CanvasRenderingContext2D | null;
      if (!ctx) return null;
      input = ctx.getImageData(0, 0, req.width, req.height);
      inputKey = hashPixels(input.data);
    }
    const key = [req.kind, hashString(req.source), JSON.stringify(req.params ?? {}), req.width, req.height, req.time ?? 0, inputKey].join('|');
    let slot = this.slots.get(slotKey);
    if (!slot) {
      slot = { key: '', image: null, pending: null, queued: null, error: null };
      this.slots.set(slotKey, slot);
    }
    if (slot.key === key) return slot.image;
    if (slot.pending === key || slot.queued?.key === key) return slot.image;
    if (slot.pending) slot.queued = { key, req, input };
    else this.send(slotKey, slot, key, req, input);
    return slot.image;
  }

  private send(slotKey: string, slot: Slot, key: string, req: ShaderRequest, input?: ImageData) {
    slot.pending = key;
    void this.call({ op: 'render', kind: req.kind, source: req.source, params: req.params, width: req.width, height: req.height, time: req.time ?? 0, input }, input ? [input.data.buffer] : []).then((r) => {
      slot.pending = null;
      slot.key = key;
      if (r.bitmap) {
        slot.image?.close();
        slot.image = r.bitmap;
        slot.error = null;
      } else slot.error = r.error ?? 'shader failed';
      this.versions.set(req.nodeId, this.versionOf(req.nodeId) + 1);
      this.version++;
      this.onChange(req.nodeId);
      const q = slot.queued;
      slot.queued = null;
      if (q && q.key !== slot.key) this.send(slotKey, slot, q.key, q.req, q.input);
    });
  }
}

function hashString(s: string) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0).toString(36) + s.length;
}

function hashPixels(d: Uint8ClampedArray) {
  const u = new Uint32Array(d.buffer, d.byteOffset, d.byteLength >> 2);
  let h = 2166136261;
  for (let i = 0; i < u.length; i++) h = Math.imul(h ^ u[i], 16777619);
  return (h >>> 0).toString(36);
}
